import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Nav } from 'office-ui-fabric-react/lib/Nav';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import styles from './LeftNav.style';
import { breakpoints } from '../../theme.style';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { can, hasRole } from '../../utils/auth';

const LeftNav = ({ isCollapsed, setIsCollapsed, className: classNameProp, ...other }) => {
  const history = useHistory();
  const { width } = useWindowDimensions();
  const isSmallScreen = width <= breakpoints.lg;

  const wrapperClassName = clsx(
    styles.wrapper,
    classNameProp,
    {
      [styles.wrapperExpanded]: !isCollapsed,
    },
    isSmallScreen && isCollapsed && styles.hidden,
  );
  const navClassName = clsx(
    styles.nav,
    {
      [styles.expanded]: !isCollapsed,
    },
    isSmallScreen && isCollapsed && styles.hidden,
  );

  const handleLinkClick = (event, link) => {
    if (!link.external) {
      event.preventDefault();
      history.push(link.url);
    }

    if (isSmallScreen) {
      setIsCollapsed(true);
    }
  };

  const getLinks = () => {
    const items = [
      {
        name: 'Home',
        url: '/',
        icon: 'Home',
        key: 'home',
      },
      {
        name: 'Partners',
        url: '/partners',
        icon: 'People',
        key: 'partners',
      },
      {
        name: 'Contracts',
        url: '/contracts',
        icon: 'TextDocument',
        key: 'contracts',
      },
      {
        name: 'Products',
        url: '/products',
        icon: 'Tag',
        key: 'products',
      },
      {
        name: 'Statements',
        url: '/statements',
        icon: 'ActivateOrders',
        key: 'statements',
      },
    ];

    if (can('viewAdjustments')) {
      items.push({
        name: 'Adjustments',
        url: '/adjustments',
        icon: 'Calculator',
        key: 'adjustments',
      });
    }

    if (can('viewDimensionLists') || can('manageDimensionLists')) {
      items.push({
        name: 'Lists',
        url: '/dimensionLists',
        icon: 'BulletedList',
        key: 'lists',
      });
    }

    if (can('viewRoles')) {
      items.push({
        name: 'Permissions',
        url: '/permissions/roles',
        icon: 'PlayerSettings',
        key: 'permissions',
      });
    }

    if (hasRole('partner')) {
      return items.filter(item => item.key !== 'partners');
    }
    return items;
  };

  function getDriLinks() {
    const driItem = {
      name: 'Operations',
      url: '/driHome/contractDebug',
      icon: 'ShieldAlert',
      key: 'driHome',
      collapseByDefault: false,
      links: [
        {
          name: 'PBI Dashboard',
          url: '/driHome/dashboard',
          key: 'pbiDashboard',
        },
        {
          name: 'Contract Debugger',
          url: '/driHome/contractDebug',
          key: 'contractDebug',
        },
        {
          name: 'Cache Eviction',
          url: '/driHome/cacheEviction',
          key: 'cacheEviction',
        },
        {
          name: 'Statement Generation',
          url: '/driHome/statementGeneration',
          key: 'statementGeneration',
        },
        {
          name: 'Statement Approval',
          url: '/driHome/statementApproval',
          key: 'statementApproval',
        },
        {
          name: 'Invoice Generation',
          url: '/driHome/invoiceGeneration',
          key: 'invoiceGeneration',
        },
        {
          name: 'Transaction Rates',
          url: '/driHome/transactionRates',
          key: 'transactionRates',
        },
        {
          name: 'Approve Dimension List',
          url: '/driHome/dimensionListApproval',
          key: 'dimensionListApproval',
        },
        {
          name: 'Statistics',
          url: '/driHome/statistics',
          key: 'statistics',
        },
      ],
    };

    if (window.config.enableDriSendEvents === true) {
      driItem.links.push({
        name: 'Send and Manage Events',
        url: '/driHome/manage-events',
        key: 'manage-events',
      });
    }

    return driItem;
  }

  function getBizOpsLink() {
    const pageLinks = [];

    if (can('generateStatement')) {
      const generateStatementLink = {
        name: 'Statement Generation',
        url: '/driHome/statementGeneration',
        key: 'generateStatement',
      };
      pageLinks.push(generateStatementLink);
    }

    if (can('approveStatement')) {
      const approveStatementLink = {
        name: 'Statement Approval',
        url: '/driHome/statementApproval',
        key: 'statementApproval',
      };
      pageLinks.push(approveStatementLink);
    }

    if (can('createInvoice')) {
      const generateInvoice = {
        name: 'Invoice Generation',
        url: '/driHome/invoiceGeneration',
        key: 'invoiceGeneration',
      };
      pageLinks.push(generateInvoice);
    }

    if (pageLinks.length === 0) {
      return {};
    }

    const pbi = [
      {
        name: 'PBI Dashboard',
        url: '/driHome/dashboard',
        key: 'pbiDashboard',
      },
    ];

    const generateStatementItem = {
      name: 'Operations',
      icon: 'ShieldAlert',
      key: 'driHome',
      collapseByDefault: false,
      links: pbi.concat(pageLinks),
    };

    return generateStatementItem;
  }

  const linkGroups = useMemo(() => {
    const groups = [];

    groups.push({
      links: getLinks(),
    });

    if (can('manageDRIOps')) {
      groups.push(getDriLinks());
    } else if (hasRole('msft')) {
      const bizOpsLink = getBizOpsLink();
      if (bizOpsLink) {
        groups.push(bizOpsLink);
      }
    }

    return groups;
  }, []);

  return (
    <div className={wrapperClassName}>
      <Nav className={navClassName} onLinkClick={handleLinkClick} groups={linkGroups} {...other} />
    </div>
  );
};

LeftNav.propTypes = {
  className: PropTypes.string,
  isCollapsed: PropTypes.bool.isRequired,
  setIsCollapsed: PropTypes.func,
};

LeftNav.defaultProps = {
  className: '',
  setIsCollapsed: () => {},
};

export default LeftNav;
